import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import type { BitrixConnection } from '@chatapp-inbox/shared';
import { saveBitrixConnection } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { bitrixErrorMessage, STATUS_LABEL } from './bitrix-text';
import { Field } from './Field';
import { applyBitrix } from './use-bitrix-admin';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './BitrixSection.module.css';

type FieldName = 'domain' | 'clientId' | 'clientSecret';

/**
 * Dados do aplicativo local do Bitrix24. Primeiro só o endereço do portal: com ele o servidor gera os
 * endereços de instalação que o Bitrix pede ao cadastrar o aplicativo. O ID e a chave só existem depois
 * desse cadastro, então os campos deles aparecem em seguida. A chave é um SEGREDO: o campo começa vazio,
 * vazio MANTÉM a guardada (ela nunca volta do servidor) e preenchida vai como foi digitada, esvaziando o
 * campo quando o servidor aceita.
 */
export function BitrixConnectionSection({
  connection,
}: {
  connection: BitrixConnection;
}): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const status = useStatusMessage();
  const configured = connection.status !== 'not_configured';
  const [domain, setDomain] = useState(connection.domain ?? '');
  const [clientId, setClientId] = useState(connection.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const refs = useRef<Partial<Record<FieldName, HTMLInputElement | null>>>({});

  const save = useMutation({
    // As variáveis (com o segredo) saem da memória do React Query assim que a mutação acaba.
    gcTime: 0,
    mutationFn: saveBitrixConnection,
    onSuccess: (saved) => applyBitrix(queryClient, saved),
  });
  const pending = save.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    const found: Partial<Record<FieldName, string>> = {};
    if (domain.trim() === '') found.domain = 'Informe o endereço do portal do Bitrix.';
    setErrors(found);
    setFormError(null);
    if (found.domain) {
      refs.current.domain?.focus();
      return;
    }
    save.mutate(
      {
        domain: domain.trim(),
        // O ID e a chave são opcionais: vazios, o que o servidor já guarda é mantido.
        ...(clientId.trim() === '' ? {} : { clientId: clientId.trim() }),
        ...(clientSecret === '' ? {} : { clientSecret }),
      },
      {
        onSuccess: (saved) => {
          save.reset(); // solta as variáveis da mutação (com o segredo) em vez de esperar a tela sair
          setClientSecret('');
          setDomain(saved.domain ?? '');
          setClientId(saved.clientId ?? '');
          status.announce(
            configured
              ? 'Dados salvos.'
              : 'Endereço salvo. Os endereços de instalação estão logo abaixo.',
          );
        },
        onError: (error) =>
          setFormError(
            bitrixErrorMessage(error, 'Não foi possível salvar os dados. Tente de novo.'),
          ),
      },
    );
  };

  const connected = connection.status === 'connected';
  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Aplicativo do Bitrix
      </h3>
      <p className={styles.state}>
        <span className={styles.term}>Situação</span>{' '}
        <span className={connected ? section.badgeOn : section.badgeOff}>
          {STATUS_LABEL[connection.status]}
        </span>
      </p>
      {connection.lastError && (
        <p role="alert" className={forms.alert}>
          {connection.lastError}
        </p>
      )}
      <p className={forms.hint}>
        {configured
          ? 'Os dados do aplicativo local que você cria no Bitrix24. A chave fica guardada criptografada no servidor e nunca é exibida de novo.'
          : 'Comece pelo endereço do portal. Com ele o CladvApp gera os endereços que o Bitrix pede ao criar o aplicativo.'}
      </p>
      <form className={forms.form} noValidate aria-busy={pending || undefined} onSubmit={submit}>
        <Field
          ref={(el) => void (refs.current.domain = el)}
          label="Endereço do portal"
          hint="Por exemplo escritorio.bitrix24.com.br. Pode colar o endereço que aparece no navegador."
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-required={true}
          value={domain}
          readOnly={pending}
          error={errors.domain}
          onChange={(event) => setDomain(event.target.value)}
        />
        {configured && (
          <>
            <Field
              ref={(el) => void (refs.current.clientId = el)}
              label="ID do aplicativo"
              hint="O Bitrix mostra depois de salvar o aplicativo local (começa com “local.”)."
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={clientId}
              readOnly={pending}
              error={errors.clientId}
              onChange={(event) => setClientId(event.target.value)}
            />
            <Field
              ref={(el) => void (refs.current.clientSecret = el)}
              label="Chave do aplicativo"
              hint={
                connection.hasSecret
                  ? 'Já guardada. Deixe em branco para manter.'
                  : 'O Bitrix mostra a chave junto do ID do aplicativo.'
              }
              type="password"
              // `new-password`: o gerenciador de senhas não deve preencher a senha DE LOGIN do usuário aqui.
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              value={clientSecret}
              readOnly={pending}
              error={errors.clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
            />
          </>
        )}
        {formError && (
          <p role="alert" className={forms.alert}>
            {formError}
          </p>
        )}
        <div className={forms.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending
              ? 'Salvando…'
              : configured
                ? 'Salvar alterações'
                : 'Gerar endereços de instalação'}
          </Button>
        </div>
        <StatusRegion message={status.message} />
      </form>
    </section>
  );
}
